import { Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useEffect, useState } from "react";
import { DashboardLayout } from "./components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "./theme";
import { Box, Typography, Button, Container, Card } from "@mui/material";
import LockPersonRoundedIcon from "@mui/icons-material/LockPersonRounded";

// Tự động gắn Authorization token cho toàn bộ các API request trong ứng dụng Admin
axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("user");
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

const LEGACY_MAP = {
    set_xs: ["set"],
    set_tx: ["set"],
    set_baccarat: ["set"],
    set_xd: ["set"],
    set_baucua: ["set"],
    set_rongho: ["set"],
    refund: ["khuyenmai", "history"],
    aff_config: ["config"],
    vip_profit: ["config"],
    vip_history: ["users"],
    notify_msg: ["notification"],
    notify_setting: ["notification"],
    cskh_chat: ["notification"],
    agency_list: ["employee"],
    staff_list: ["employee"],
    staff_rbac: ["employee"],
    api_report: ["api_game"],
    api_transfer: ["api_game"],
    api_lobbies: ["api_game"],
    api_presets: ["api_game"],
    site_custom: ["config"],
    site_setting: ["config"],
    site_bank: ["bank"],
    site_login_logs: ["logs"],
    site_online_users: ["users"],
    approval_history: ["recharge", "withdraw", "money"],
};

// Khớp đường dẫn URL với quyền tương ứng
const getRequiredPermission = (pathname) => {
    const path = String(pathname || "").toLowerCase().replace(/\/$/, "");
    if (!path || path === "/admin") return null;

    if (path.startsWith("/admin/add")) return "recharge";
    if (path.startsWith("/admin/request")) return "withdraw";
    if (path.startsWith("/admin/approval-history")) return "approval_history";
    if (path.startsWith("/admin/allusers")) return "edit";
    if (path.startsWith("/admin/users")) return "users";

    if (path.includes("settx") || path.includes("set-tx")) return "set_tx";
    if (path.includes("setxd") || path.includes("set-xd")) return "set_xd";
    if (path.includes("setbaccarat") || path.includes("set-baccarat")) return "set_baccarat";
    if (path.includes("setbaucua") || path.includes("set-baucua")) return "set_baucua";
    if (path.includes("setrongho") || path.includes("set-rongho")) return "set_rongho";
    if (path.includes("setxs") || path.includes("set-xs")) return "set_xs";

    if (path.startsWith("/admin/historyapi")) return "api_report";
    if (path.startsWith("/admin/report-api")) return "api_transfer";
    if (path.startsWith("/admin/lobbies")) return "api_lobbies";
    if (path.startsWith("/admin/game-presets")) return "api_presets";

    if (path.startsWith("/admin/history")) return "history";
    if (path.startsWith("/admin/refund")) return "refund";
    if (path.startsWith("/admin/khuyenmai")) return "khuyenmai";
    if (path.startsWith("/admin/promo")) return "giftcode";

    if (path.startsWith("/admin/setting/money")) return "aff_config";
    if (path.startsWith("/admin/setting/profit")) return "vip_profit";
    if (path.startsWith("/admin/notification")) return "notify_msg";
    if (path.startsWith("/admin/settingnotify")) return "notify_setting";
    if (path.startsWith("/admin/chat")) return "cskh_chat";

    if (path.startsWith("/admin/allnv")) return "agency_list";
    if (path.startsWith("/admin/admin-list")) return "staff_list";
    if (path.startsWith("/admin/permissions")) return "staff_rbac";
    if (path.startsWith("/admin/audit-logs")) return "logs";

    if (path.startsWith("/admin/config")) return "site_custom";
    if (path.startsWith("/admin/setting")) return "site_setting";
    if (path.startsWith("/admin/bank")) return "site_bank";
    if (path.startsWith("/admin/login-history")) return "site_login_logs";
    if (path.startsWith("/admin/online-users")) return "site_online_users";

    return null;
};

const checkUserPermission = (profile, requiredPerm) => {
    if (!requiredPerm) return true;
    if (!profile) return false;
    // Super Admin hoặc Full quyền luôn được truy cập
    if (profile.iduser === 1 || profile.permission?.full == 1 || profile.permission?.full === true) return true;

    const perms = profile.permission || {};
    const keysToCheck = Array.isArray(requiredPerm) ? requiredPerm : [requiredPerm];

    return keysToCheck.some((k) => {
        if (Number(perms[k]) === 1 || perms[k] === true) return true;
        if (Number(perms[k]) === 0 || perms[k] === false) return false;
        if (LEGACY_MAP[k]) {
            return LEGACY_MAP[k].some((lk) => Number(perms[lk]) === 1 || perms[lk] === true);
        }
        return false;
    });
};

const PrivateRouteAdmin = () => {
    const [checking, setChecking] = useState(true);
    const [isAdmin, setIsAdmin] = useState(null);
    const [isCustomer, setIsCustomer] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem("user");
        if (!token) {
            setChecking(false);
            setIsAdmin(false);
            setIsCustomer(false);
            return;
        }

        axios
            .get(`${process.env.REACT_APP_API_URL}/auth/getUser`)
            .then((res) => {
                setChecking(false);
                if (res.data && res.data.data) {
                    setIsAdmin(res.data.data.isAdmin || false);
                    setIsCustomer(res.data.data.isCustomer || false);
                    if (res.data.data.isAdmin || res.data.data.isCustomer) {
                        localStorage.setItem("profile", JSON.stringify(res.data.data));
                    }
                } else {
                    setIsAdmin(false);
                    setIsCustomer(false);
                }
            })
            .catch(() => {
                setChecking(false);
                if (token) {
                    setIsAdmin(true);
                } else {
                    setIsAdmin(false);
                    setIsCustomer(false);
                }
            });
    }, []);

    if (checking) {
        return null;
    }

    if (!isAdmin) {
        return isCustomer ? <Navigate to="/customer" replace /> : <Navigate to="/login" replace />;
    }

    // Kiểm tra phân quyền truy cập URL hiện tại
    const profile = JSON.parse(localStorage.getItem("profile") || "{}");
    const requiredPerm = getRequiredPermission(location.pathname);
    const hasPermission = checkUserPermission(profile, requiredPerm);

    if (!hasPermission) {
        return (
            <ThemeProvider theme={theme}>
                <DashboardLayout>
                    <Box sx={{ flexGrow: 1, py: 6, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh" }}>
                        <Container maxWidth="sm">
                            <Card sx={{ p: 4, textAlign: "center", borderRadius: 3, boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
                                <LockPersonRoundedIcon sx={{ fontSize: 72, color: "#ef4444", mb: 2 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
                                    Không có quyền truy cập
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#64748b", mb: 3, lineHeight: 1.6 }}>
                                    Tài khoản của bạn chưa được cấp quyền để truy cập trang này. Vui lòng liên hệ quản trị viên cấp cao để được cấp quyền.
                                </Typography>
                                <Button variant="contained" color="primary" onClick={() => navigate("/admin")} sx={{ borderRadius: "8px", textTransform: "none", px: 3, py: 1 }}>
                                    Quay lại Trang chủ
                                </Button>
                            </Card>
                        </Container>
                    </Box>
                </DashboardLayout>
            </ThemeProvider>
        );
    }

    return <Outlet />;
};

export default PrivateRouteAdmin;
